import React, { useContext, useEffect, useState } from "react";
import {
  Box,
  Typography,
  IconButton,
  List,
  ListItem,
  ListItemText,
  useTheme,
  useMediaQuery,
  Drawer,
  AppBar,
  Toolbar,
} from "@mui/material";
import DashboardIcon from "@mui/icons-material/Dashboard";
import GroupIcon from "@mui/icons-material/Group";
import FlightIcon from "@mui/icons-material/Flight";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import SettingsIcon from "@mui/icons-material/Settings";
import MenuIcon from "@mui/icons-material/Menu";
import LocalHospitalIcon from "@mui/icons-material/LocalHospital";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import ShieldIcon from "@mui/icons-material/Shield";
import HealthAndSafetyIcon from "@mui/icons-material/HealthAndSafety";
import Header from "./components/Header";
import { CATEGORIES_TYPES, images, constants } from "common";
import {
  deleteUserAndData,
  getDashboardStats,
  getUsersByCompany,
} from "services/firebaseServices";
import { getProfile } from "utilities";
import { collection, getDocs, query, where } from "firebase/firestore";
import { fireStoreDB } from "services";
import "bootstrap/dist/css/bootstrap.css";
import { ApplicationContext } from "shared";
import BusinessDashboardComponent from "./dashboard";
import BusinessSetting from "./setting";
import EmployeesComponent from "./employees";
import RiskPage from "./travel-insights";
import TravelPage from "./travel";
import CardTracking from "./card-tracking";
import Emergencies from "./emergencies";
import Insurance from "./insurance";
import Compliance from "./health-completeness";

// Feature flag — hide the Insurance tab until it's ready to launch.
const INSURANCE_ENABLED = process.env.REACT_APP_INSURANCE_ENABLED === "true";

const drawerItems = [
  { section: "Overview" },
  { icon: <DashboardIcon />, text: "Dashboard", value: "dashboard" },
  { icon: <GroupIcon />, text: "Employees", value: "employees" },
  { icon: <HealthAndSafetyIcon />, text: "Compliance", value: "compliance" },
  { section: "Operations" },
  { icon: <FlightIcon />, text: "Travel", value: "travel" },
  { icon: <WarningAmberIcon />, text: "Risk", value: "risk" },
  { icon: <LocalHospitalIcon />, text: "Emergencies", value: "emergencies" },
  { icon: <CreditCardIcon />, text: "Card Tracking", value: "cardTracking" },
  ...(INSURANCE_ENABLED
    ? [{ icon: <ShieldIcon />, text: "Insurance", value: "insurance" }]
    : []),
  { section: "Account" },
  { icon: <SettingsIcon />, text: "Settings", value: "settings" },
];

export default function BusinessDashboard() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isTablet = useMediaQuery(theme.breakpoints.down("lg"));
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [users, setUsers] = useState([]);
  const [businessData, setBusinessData] = useState(null);
  const [deleteModel, setDeleteModel] = useState(false);
  const [showModal, setShowModal] = useState(false);
  // 🔥 Fetch business info from Firestore

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedSidebrItem, setSelectedSidebarItem] = useState("dashboard");

  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    const formattedName = businessData.companyName
      .toLowerCase()
      .replace(/\s+/g, "-");
    const url = `${constants.siteBaseURL}/${formattedName}/sign-up`;

    navigator.clipboard.writeText(url).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500); // Tooltip hides after 1.5s
    });
  };
  const handleDeleteConfirm = async () => {
    // setShowDeleteModal(false);

    let res = deleteUserAndData(showDeleteModal);
    if (res) {
      fetchUsers();
      setShowDeleteModal(false);
    }
    // 🔥 Call your delete API or Firestore delete logic here
  };
  const { categoryListing, setCategoryListing } =
    useContext(ApplicationContext);
  const fetchRecords = async (users) => {
    try {
      const tempData = [...categoryListing];
      if (!!users) {
        tempData[0].dataListing = await getDashboardStats(
          CATEGORIES_TYPES.diagnoses,
          users,
        );
        tempData[1].dataListing = await getDashboardStats(
          CATEGORIES_TYPES.medicines,
          users,
        );
        tempData[2].dataListing = await getDashboardStats(
          CATEGORIES_TYPES.allergies,
          users,
        );
        tempData[3].dataListing = await getDashboardStats(
          CATEGORIES_TYPES.vaccines,
          users,
        );
      }
      setCategoryListing(tempData);
    } catch (err) {
      console.log(err);
    }
  };
  const fetchBusinessInfo = async () => {
    try {
      const profile = getProfile();
      if (!profile?.uid) return;

      const q = query(
        collection(fireStoreDB, "businesses"),
        where("userId", "==", profile.uid),
      );
      const snapshot = await getDocs(q);

      if (!snapshot.empty) {
        setBusinessData(snapshot.docs[0].data());
      }
    } catch (error) {
      console.error("Error fetching business info:", error);
    }
  };

  useEffect(() => {
    fetchBusinessInfo();
  }, []);
  const fetchUsers = async () => {
    try {
      const result = await getUsersByCompany(businessData?.companyName);
      setUsers(result);
      fetchRecords(result);
    } catch (error) {
      console.error("Error fetching company users:", error);
    }
  };
  useEffect(() => {
    fetchUsers();
  }, [businessData?.companyName]); // 👈 dependency in case companyName changes
  const handleNavigation = (path) => {
    setSelectedSidebarItem(path);
    setMobileOpen(false);
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const healthScoreOptions = {
    chart: {
      type: "area",
      height: 200,
      backgroundColor: "transparent",
    },
    title: { text: null },

    xAxis: {
      labels: { enabled: false },
      gridLineWidth: 0,
      lineWidth: 0,
      tickLength: 0,
    },
    yAxis: {
      labels: {
        enabled: false, // ✅ show Y-axis labels
        style: {
          color: "#555", // optional: make it visible on light bg
          fontSize: "10px",
        },
      },
      gridLineWidth: 0.3, // optional: subtle grid lines
      lineWidth: 0,
      tickLength: 0,
      title: { text: null }, // remove default title
    },
    tooltip: {
      pointFormat: "{series.name}: <b>{point.y}</b>",
    },
    legend: { enabled: false },
    credits: { enabled: false },
    plotOptions: {
      area: {
        marker: { enabled: false },
        fillColor: {
          linearGradient: [0, 0, 0, 150],
          stops: [
            [0, "#1976d2"],
            [1, "rgba(25, 118, 210, 0.1)"],
          ],
        },
        lineColor: "#1976d2",
        lineWidth: 2,
      },
    },
    series: [
      {
        name: "Health Data",
        data: categoryListing.map((item) => item.dataListing?.length || 0),
        color: "#1976d2",
      },
    ],
  };

  // const healthScoreOptions = {
  //   chart: {
  //     type: 'area',
  //     height: 200,
  //     backgroundColor: 'transparent',
  //   },
  //   title: { text: null },
  //   xAxis: {
  //     labels: { enabled: false },
  //     gridLineWidth: 0,
  //     lineWidth: 0,
  //     tickLength: 0,
  //   },
  //   yAxis: {
  //     labels: { enabled: false },
  //     title: { text: null },
  //     gridLineWidth: 0,
  //   },
  //   tooltip: { enabled: false },
  //   legend: { enabled: false },
  //   credits: { enabled: false },
  //   plotOptions: {
  //     area: {
  //       marker: { enabled: false },
  //       fillColor: {
  //         linearGradient: [0, 0, 0, 150],
  //         stops: [
  //           [0, '#1976d2'],
  //           [1, 'rgba(25, 118, 210, 0.1)'],
  //         ],
  //       },
  //       lineColor: '#1976d2',
  //       lineWidth: 2,
  //     },
  //   },
  //   series: [
  //     {
  //       data: [5, 7, 9, 18, 13, 21, 20, 24, 19, 26, 32, 28, 26, 30, 35],
  //       color: '#1976d2',
  //     },
  //   ],
  // };

  const dayWiseCounts = {};

  categoryListing.forEach((category) => {
    category.dataListing.forEach((entry) => {
      const date = entry.date || "Unknown";
      dayWiseCounts[date] = (dayWiseCounts[date] || 0) + 1;
    });
  });

  console.log("users", users);
  const metrics = [
    {
      label: "Diagnoses reported",
      value:
        categoryListing.find((item) => item.mainTitle === "Diagnoses")
          ?.dataListing?.length || 0,
      color: "#EA4FC640",
      icon: images.info,
    },
    {
      label: "Medications reported",
      value:
        categoryListing.find((item) => item.mainTitle === "Medicines")
          ?.dataListing?.length || 0,
      color: "#67CBFF40",
      icon: images.scale,
    },
    {
      label: "Allergies Reported",
      value:
        categoryListing.find((item) => item.mainTitle === "Allergies")
          ?.dataListing?.length || 0,
      color: "#FFD36A40",
      icon: images.cup,
    },
    {
      label: "Vaccines reported",
      value:
        categoryListing.find((item) => item.mainTitle === "Vaccines")
          ?.dataListing?.length || 0,
      color: "#7B74FF40",
      icon: images.pill,
    },
  ];
  const companyInitials = (businessData?.companyName || "W M")
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase())
    .join("");

  const drawer = (
    <Box
      sx={{
        width: "100%",
        bgcolor: "#fff",
        border: "1px solid #EAECF0",
        borderRadius: 3,
        p: 1.5,
      }}
    >
      {/* Company block */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, px: 1, py: 1 }}>
        <Box
          sx={{
            width: 38,
            height: 38,
            borderRadius: 2,
            bgcolor: "#32ABF8",
            color: "#fff",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontWeight: 700,
            fontSize: 14,
            flexShrink: 0,
          }}
        >
          {companyInitials}
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography
            sx={{
              fontSize: 14,
              fontWeight: 700,
              color: "#101828",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {businessData?.companyName || "Your Company"}
          </Typography>
          <Typography sx={{ fontSize: 11.5, color: "#667085" }}>
            Business workspace
          </Typography>
        </Box>
      </Box>

      <Box sx={{ height: "1px", bgcolor: "#F2F4F7", mx: 0.5, my: 1 }} />

      <List sx={{ width: "100%", p: 0 }}>
        {drawerItems.map((item, idx) => {
          if (item.section) {
            return (
              <Typography
                key={idx}
                variant="caption"
                sx={{
                  display: "block",
                  px: 1.5,
                  pt: idx === 0 ? 0.5 : 2,
                  pb: 0.75,
                  color: "#98A2B3",
                  fontWeight: 700,
                  fontSize: 11,
                  letterSpacing: 0.6,
                  textTransform: "uppercase",
                }}
              >
                {item.section}
              </Typography>
            );
          }
          const isSelected = selectedSidebrItem === item.value;
          return (
            <ListItem
              key={idx}
              button
              onClick={() => handleNavigation(item.value)}
              sx={{
                position: "relative",
                borderRadius: 2,
                mb: 0.25,
                py: 1,
                px: 1.25,
                gap: 1.25,
                color: isSelected ? "#0B6FB0" : "#475467",
                bgcolor: isSelected ? "#EAF6FE" : "transparent",
                "&:hover": { bgcolor: isSelected ? "#EAF6FE" : "#F9FAFB" },
              }}
            >
              {isSelected && (
                <Box
                  sx={{
                    position: "absolute",
                    left: 0,
                    top: 8,
                    bottom: 8,
                    width: 3,
                    borderRadius: 2,
                    bgcolor: "#32ABF8",
                  }}
                />
              )}
              <Box
                sx={{
                  display: "flex",
                  color: isSelected ? "#1E91D6" : "#98A2B3",
                  "& svg": { fontSize: 20 },
                }}
              >
                {item.icon}
              </Box>
              <ListItemText
                primary={item.text}
                primaryTypographyProps={{
                  fontSize: 14,
                  fontWeight: isSelected ? 600 : 500,
                }}
              />
            </ListItem>
          );
        })}
      </List>
    </Box>
  );

  const renderContent = () => {
    switch (selectedSidebrItem) {
      case "employees":    return <EmployeesComponent />;
      case "compliance":   return <Compliance />;
      case "travel":       return <TravelPage />;
      case "risk":         return <RiskPage totalTravelingEmployees={users?.length || 0} />;
      case "emergencies":  return <Emergencies />;
      case "cardTracking": return <CardTracking />;
      case "insurance":    return <Insurance />;
      case "settings":     return <BusinessSetting />;
      default:             return <BusinessDashboardComponent onNavigate={handleNavigation} />;
    }
  };

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#F7F8FA" }}>
      <Header businessData={businessData} />

      {/* Mobile App Bar */}
      {isMobile && (
        <AppBar
          position="fixed"
          sx={{
            backgroundColor: "white",
            color: "black",
            boxShadow: "none",
            borderBottom: "1px solid #e0e0e0",
          }}
        >
          <Toolbar>
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ mr: 2 }}
            >
              <MenuIcon />
            </IconButton>
            <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
              Dashboard
            </Typography>
          </Toolbar>
        </AppBar>
      )}

      <Box sx={{ display: "flex", mt: isMobile ? 8 : 0 }}>
        {/* Sidebar - Desktop */}
        {!isMobile && (
          <Box
            sx={{
              width: 248,
              flexShrink: 0,
              p: 2,
              pr: 1,
              mt: 3,
              display: { xs: "none", md: "block" },
              position: "sticky",
              top: 84,
              alignSelf: "flex-start",
            }}
          >
            {drawer}
          </Box>
        )}

        {/* Sidebar - Mobile */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile.
          }}
          sx={{
            display: { xs: "block", md: "none" },
            "& .MuiDrawer-paper": {
              boxSizing: "border-box",
              width: 240,
            },
          }}
        >
          {drawer}
        </Drawer>

        {/* Main Content */}
        <Box
          sx={{
            flexGrow: 1,
            minWidth: 0,
            ml: { xs: 0, md: 1 },
            mr: { xs: 0, md: 3 },
            mt: { xs: 2, md: 3 },
            p: { xs: 1, md: 2 },
          }}
        >
          {renderContent()}
        </Box>
      </Box>
    </Box>
  );
}
